import React, { useEffect, useMemo, useRef, useState } from "react"
import InputText from "../inputText/inputText"
import styles from "./hotelRates.module.scss"
import { PerPersonSeasonalSurchargeModel } from "../../models"
import { toTitleCase } from "util/textcase"

type SeasonOption = { id: string; name: string }

type Props = {
    seasons: SeasonOption[]
    // NOTE: runtime data may come in slightly different shapes; normalize in component
    perPersonSeasonalSurcharge?: Array<PerPersonSeasonalSurchargeModel | any>
    onChange?: (next: PerPersonSeasonalSurchargeModel[]) => void
    readOnly?: boolean
    showValidationErrors?: boolean
}

const toPositiveNumberFromUnknownOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null
    const n = typeof v === "number" ? v : Number(v)
    return Number.isFinite(n) && n >= 0 ? n : null
}

const toPositiveNumberOrNull = (v: string): number | null => {
    const trimmed = v.trim()
    if (!trimmed) return null
    const n = parseFloat(trimmed)
    return Number.isFinite(n) && n >= 0 ? n : null
}

const getSeasonId = (row: any): string | null => {
    const id = row?.season_id ?? row?.seasonId ?? null
    return id != null ? String(id) : null
}

const buildDraftMap = (seasons: SeasonOption[], fromContract: Array<PerPersonSeasonalSurchargeModel | any> | undefined) => {
    const bySeasonId: Record<string, PerPersonSeasonalSurchargeModel> = {}
    const list = Array.isArray(fromContract) ? fromContract : []

    seasons.forEach((season) => {
        const existing = list.find((p) => getSeasonId(p) === season.id)
        bySeasonId[season.id] = {
            per_person_seasonal_surcharge_id:
                (existing as any)?.per_person_seasonal_surcharge_id ??
                (existing as any)?.perPersonSeasonalSurchargeId ??
                null,
            season_id: season.id,
            season_name: (existing as any)?.season_name ?? (existing as any)?.seasonName ?? season.name,
            adult_surcharge: toPositiveNumberFromUnknownOrNull((existing as any)?.adult_surcharge ?? (existing as any)?.adultSurcharge),
            child_surcharge: toPositiveNumberFromUnknownOrNull((existing as any)?.child_surcharge ?? (existing as any)?.childSurcharge),
        }
    })

    return bySeasonId
}

const PerPersonSeasonalSurcharges: React.FC<Props> = ({
    seasons,
    perPersonSeasonalSurcharge,
    onChange,
    readOnly = false,
    showValidationErrors = false,
}) => {
    const syncKey = useMemo(() => JSON.stringify({ seasons, perPersonSeasonalSurcharge: perPersonSeasonalSurcharge ?? [] }), [seasons, perPersonSeasonalSurcharge])
    const lastSyncedKey = useRef<string>("")

    const [draftBySeasonId, setDraftBySeasonId] = useState<Record<string, PerPersonSeasonalSurchargeModel>>(() =>
        buildDraftMap(seasons, perPersonSeasonalSurcharge)
    )

    useEffect(() => {
        if (syncKey === lastSyncedKey.current) return
        lastSyncedKey.current = syncKey
        setDraftBySeasonId(buildDraftMap(seasons, perPersonSeasonalSurcharge))
    }, [syncKey, seasons, perPersonSeasonalSurcharge])

    const commit = (nextDraft: Record<string, PerPersonSeasonalSurchargeModel>) => {
        setDraftBySeasonId(nextDraft)
        if (!onChange) return

        const nextArray: PerPersonSeasonalSurchargeModel[] = seasons.map((s) => {
            const row = nextDraft[s.id]
            return {
                per_person_seasonal_surcharge_id: row?.per_person_seasonal_surcharge_id ?? null,
                season_id: s.id,
                season_name: row?.season_name ?? s.name,
                adult_surcharge: row?.adult_surcharge ?? null,
                child_surcharge: row?.child_surcharge ?? null,
            }
        })

        onChange(nextArray)
    }

    return (
        <div className={styles.peakRoomSurchargeCard}>
            <div className={styles.peakRoomSurchargeHeader}>
                <div>
                    <div className={styles.peakRoomSurchargeTitle}>Per-Person Seasonal Surcharges</div>
                </div>
            </div>

            <div className={styles.peakRoomSurchargeBody}>
                {/* <p className={styles.perPersonSeasonalSurchargeDescription}>
                    Configure per-person surcharges for each season. These apply when occupancy exceeds the base room capacity.
                </p> */}

                <div className={styles.extraBedTableContainer}>
                    <table className={`${styles.extraBedTable} ${styles.perPersonSeasonalSurchargeTable}`}>
                        <thead>
                            <tr>
                                <th>Season</th>
                                <th>
                                    Adult Surcharge (VND/Person/Night) <span className={styles.required}>*</span>
                                </th>
                                <th>
                                    Child Surcharge (VND/Person/Night) <span className={styles.required}>*</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {seasons.map((season) => {
                                const row = draftBySeasonId[season.id]
                                const adultVal = row?.adult_surcharge ?? null
                                const childVal = row?.child_surcharge ?? null

                                return (
                                    <tr key={season.id}>
                                        <td>{toTitleCase(season.name)}</td>
                                        <td className={styles.perPersonSeasonalSurchargePriceCell}>
                                            <InputText
                                                label={`Adult Surcharge - ${season.name}`}
                                                hideLabel={true}
                                                noMargin={true}
                                                
                                                placeholder="Enter amount"
                                                value={adultVal === null || adultVal === undefined ? "" : String(adultVal)}
                                                onChange={(e) => {
                                                    const val = toPositiveNumberOrNull(e.target.value)
                                                    const next = {
                                                        ...draftBySeasonId,
                                                        [season.id]: {
                                                            ...draftBySeasonId[season.id],
                                                            season_name: season.name,
                                                            adult_surcharge: val,
                                                        },
                                                    }
                                                    commit(next)
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault()
                                                }}
                                                type="number"
                                                disabled={readOnly}
                                                error={showValidationErrors && (adultVal === null || adultVal === undefined) ? "Required" : undefined}
                                            />
                                        </td>
                                        <td className={styles.perPersonSeasonalSurchargePriceCell}>
                                            <InputText
                                                label={`Child Surcharge - ${season.name}`}
                                                hideLabel={true}
                                                noMargin={true}
                                                placeholder="Enter amount"
                                                value={childVal === null || childVal === undefined ? "" : String(childVal)}
                                                onChange={(e) => {
                                                    const val = toPositiveNumberOrNull(e.target.value)
                                                    const next = {
                                                        ...draftBySeasonId,
                                                        [season.id]: {
                                                            ...draftBySeasonId[season.id],
                                                            season_name: season.name,
                                                            child_surcharge: val,
                                                        },
                                                    }
                                                    commit(next)
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault()
                                                }}
                                                type="number"
                                                disabled={readOnly}
                                                error={showValidationErrors && (childVal === null || childVal === undefined) ? "Required" : undefined}
                                            />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default PerPersonSeasonalSurcharges
